/*
 * GVS Adaptive Media Slideshow — IMG1C.3
 * True media-ratio stage + final transition QA.
 *
 * Core rules:
 * - No multi-slide transform track.
 * - Foreground image/video is always contained and therefore never cropped.
 * - Desktop stage follows the active creative's true aspect ratio without a
 *   normal fixed-height ceiling, allowing complete media to meet both edges.
 * - Transitions are staged: foreground out -> ambient/stage dissolve ->
 *   foreground in. Competing promotional text is never crossfaded together.
 * - Responsive images remain native <picture>; JS only manages active video,
 *   adaptive stage sizing, navigation, transition state and autoplay.
 */
(() => {
  if (customElements.get('gvs-adaptive-slideshow')) return;

  class GvsAdaptiveSlideshow extends HTMLElement {
    constructor() {
      super();
      this.abortController = new AbortController();
      this.current = 0;
      this.timer = null;
      this.resizeTimer = null;
      this.startX = 0;
      this.startY = 0;
      this.didSwipe = false;
      this.isInViewport = true;
      this.userPaused = false;
      this.pointerPaused = false;
      this.isTransitioning = false;
      this.pendingNavigation = null;
      this.transitionTarget = null;
      this.device = '';
    }

    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      if (this.abortController.signal.aborted) this.abortController = new AbortController();
      this.dataset.initialised = 'true';

      this.stage = this.querySelector('[data-gvs-stage]');
      this.currentLayer = this.querySelector('[data-gvs-current-layer]');
      this.incomingLayer = this.querySelector('[data-gvs-incoming-layer]');
      this.templates = Array.from(this.querySelectorAll('template[data-gvs-slide-template]'));
      this.dots = Array.from(this.querySelectorAll('[data-gvs-dot]'));
      this.previousButton = this.querySelector('[data-gvs-previous]');
      this.nextButton = this.querySelector('[data-gvs-next]');
      this.autoplayButton = this.querySelector('[data-gvs-autoplay]');
      this.status = this.querySelector('[data-gvs-status]');

      this.total = this.templates.length;
      this.loop = this.dataset.loop === 'true';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayMobile = this.dataset.autoplayMobile === 'true';
      this.autoplaySpeed = Math.max(1800, Number(this.dataset.autoplaySpeed) || 4500);
      this.transitionStyle = this.dataset.transitionStyle || 'slide';
      this.baseTransitionMs = Math.max(180, Number(this.dataset.transitionSpeed) || 350);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.device = this.getDevice();

      if (!this.stage || !this.currentLayer || !this.incomingLayer || this.total === 0) return;

      this.updateAutoplayAvailability();

      this.applyMotionDuration();
      this.bindEvents();
      this.observeViewport();
      this.updateState(false);

      const initialArticle = this.getCurrentArticle();
      this.activateArticle(initialArticle, { initial: true });
      this.scheduleInitialAmbientHydration(initialArticle);

      // Only desktop uses JS-managed true media-ratio stage geometry. Mobile
      // and tablet already have stable CSS stage heights, so do not explicitly
      // wait for/decode the LCP poster there; let the browser paint it naturally.
      // This removes needless decode work from the mobile LCP window.
      if (this.device === 'desktop') {
        this.syncStageToArticle(initialArticle, { animate: false }).catch(() => {});
        this.waitForPoster(initialArticle)
          .then(() => this.syncStageToArticle(initialArticle, { animate: false }))
          .catch(() => {});
      }

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.clearTimeout(this.resizeTimer);
      this.abortController.abort();
      this.intersectionObserver?.disconnect();
      this.pauseArticleMedia(this.getCurrentArticle(), false);
      this.pauseArticleMedia(this.getIncomingArticle(), false);
      delete this.dataset.initialised;
    }

    get signal() {
      return this.abortController.signal;
    }

    getDevice() {
      if (window.matchMedia('(max-width: 749px)').matches) return 'mobile';
      if (window.matchMedia('(max-width: 1199px)').matches) return 'tablet';
      return 'desktop';
    }

    canAutoplaySlides() {
      return Boolean(
        this.autoplayEnabled &&
        !this.reducedMotion &&
        (this.device !== 'mobile' || this.autoplayMobile)
      );
    }

    updateAutoplayAvailability() {
      if (!this.autoplayButton) return;
      this.autoplayButton.hidden = !this.canAutoplaySlides();
    }

    getPhaseDurations() {
      if (this.reducedMotion) {
        return { out: 0, ambient: 0, incoming: 0, stage: 0 };
      }

      let pace = this.baseTransitionMs;
      if (this.device === 'desktop') pace = Math.max(pace + 55, 380);
      else if (this.device === 'tablet') pace = Math.max(pace + 20, 330);
      else pace = Math.max(pace - 35, 270);

      // The legacy "fade" choice is retained as a slightly quicker staged fade.
      // The old "slide" value now represents the recommended staged dissolve.
      if (this.transitionStyle === 'fade') pace *= 0.8;

      return {
        out: Math.round(pace * 0.26),
        ambient: Math.round(pace * 0.44),
        incoming: Math.round(pace * 0.39),
        stage: Math.round(pace * 0.48)
      };
    }

    applyMotionDuration() {
      const phases = this.getPhaseDurations();
      this.style.setProperty('--gvs-as-foreground-out-ms', `${phases.out}ms`);
      this.style.setProperty('--gvs-as-ambient-ms', `${phases.ambient}ms`);
      this.style.setProperty('--gvs-as-foreground-in-ms', `${phases.incoming}ms`);
      this.style.setProperty('--gvs-as-stage-ms', `${phases.stage}ms`);
    }

    getCurrentArticle() {
      return this.currentLayer?.querySelector('[data-adaptive-slide]') || null;
    }

    getIncomingArticle() {
      return this.incomingLayer?.querySelector('[data-adaptive-slide]') || null;
    }

    bindEvents() {
      const options = { signal: this.signal };

      this.previousButton?.addEventListener('click', () => {
        this.navigateRelative(-1, { userInitiated: true });
      }, options);

      this.nextButton?.addEventListener('click', () => {
        this.navigateRelative(1, { userInitiated: true });
      }, options);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goTo(Number(dot.dataset.index) || 0, {
            direct: true,
            userInitiated: true
          });
        }, options);
      });

      this.autoplayButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.updateAutoplayButton();

        if (this.userPaused) {
          this.stopAutoplay();
          this.pauseArticleMedia(this.getCurrentArticle(), false);
        } else {
          this.playActiveVideo();
          this.startAutoplay();
        }
      }, options);

      // LAUNCH1E1 contract: keyboard focus entering autoplay content is an
      // explicit persistent pause, never a temporary pause that silently resumes.
      this.addEventListener('focusin', () => {
        if (!this.canAutoplaySlides() || this.userPaused) return;
        this.userPaused = true;
        this.stopAutoplay();
        this.pauseArticleMedia(this.getCurrentArticle(), false);
        this.updateAutoplayButton();
      }, options);

      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.addEventListener('mouseenter', () => {
          this.pointerPaused = true;
          this.stopAutoplay();
        }, options);

        this.addEventListener('mouseleave', () => {
          this.pointerPaused = false;
          this.playActiveVideo();
          this.startAutoplay();
        }, options);
      }

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.navigateRelative(-1, { userInitiated: true });
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigateRelative(1, { userInitiated: true });
        }
      }, options);

      this.stage.addEventListener('touchstart', (event) => {
        const touch = event.touches[0];
        this.startX = touch.clientX;
        this.startY = touch.clientY;
        this.didSwipe = false;
        this.stopAutoplay();
      }, { passive: true, signal: this.signal });

      this.stage.addEventListener('touchmove', (event) => {
        const touch = event.touches[0];
        const deltaX = touch.clientX - this.startX;
        const deltaY = touch.clientY - this.startY;
        if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.didSwipe = true;
        }
      }, { passive: true, signal: this.signal });

      this.stage.addEventListener('touchend', (event) => {
        const deltaX = this.startX - event.changedTouches[0].clientX;

        if (this.didSwipe && Math.abs(deltaX) > 46) {
          this.navigateRelative(deltaX > 0 ? 1 : -1, { userInitiated: true });
        } else {
          this.startAutoplay();
        }
      }, { passive: true, signal: this.signal });

      this.stage.addEventListener('click', (event) => {
        if (!this.didSwipe) return;
        event.preventDefault();
        event.stopPropagation();
        this.didSwipe = false;
      }, { capture: true, signal: this.signal });

      window.addEventListener('resize', () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.handleDeviceChange(), 120);
      }, { passive: true, signal: this.signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
          this.pauseArticleMedia(this.getCurrentArticle(), false);
        } else {
          this.playActiveVideo();
          this.startAutoplay();
        }
      }, options);

      document.addEventListener('shopify:block:select', (event) => {
        const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
        if (!blockId) return;

        const template = this.templates.find((item) => item.dataset.blockId === blockId);
        if (!template) return;

        const index = Number(template.dataset.index);
        if (!Number.isFinite(index)) return;

        this.userPaused = true;
        this.updateAutoplayButton();
        this.goTo(index, { direct: true, userInitiated: false });
      }, options);
    }

    observeViewport() {
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.isInViewport = Boolean(entry?.isIntersecting);

        if (this.isInViewport) {
          this.playActiveVideo();
          this.startAutoplay();
        } else {
          this.stopAutoplay();
          this.pauseArticleMedia(this.getCurrentArticle(), false);
        }
      }, { threshold: 0.15 });

      this.intersectionObserver.observe(this);
    }

    handleDeviceChange() {
      if (this.isTransitioning) {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.handleDeviceChange(), 100);
        return;
      }

      const nextDevice = this.getDevice();
      const article = this.getCurrentArticle();

      if (nextDevice === this.device) {
        if (this.device === 'desktop') {
          this.syncStageToArticle(article, { animate: false }).catch(() => {});
        }
        return;
      }

      this.stopAutoplay();
      this.device = nextDevice;
      this.updateAutoplayAvailability();
      this.applyMotionDuration();

      // If a visitor starts on mobile and later expands/rotates into a larger
      // viewport, restore the decorative ambient at idle priority.
      if (this.device !== 'mobile') {
        this.scheduleInitialAmbientHydration(article);
      }

      if (this.device !== 'desktop') {
        this.stage.style.removeProperty('--gvs-as-stage-height');
      }

      // <picture> switches its responsive image by itself. Only the video needs
      // replacement, so the responsive poster/image remains continuously visible.
      if (!article) return;

      this.pauseArticleMedia(article, false);
      article.classList.remove('is-video-visible', 'has-video-controls');
      article.querySelectorAll('.gvs-adaptive-slideshow__video').forEach((video) => video.remove());
      this.activateArticle(article, { deviceRefresh: true });

      this.syncStageToArticle(article, { animate: false }).catch(() => {});
      this.waitForPoster(article)
        .then(() => this.syncStageToArticle(article, { animate: false }))
        .catch(() => {});
      this.startAutoplay();
    }

    getNavigationBase() {
      if (this.pendingNavigation) return this.normaliseIndex(this.pendingNavigation.index);
      if (Number.isFinite(this.transitionTarget)) return this.transitionTarget;
      return this.current;
    }

    navigateRelative(delta, { userInitiated = false } = {}) {
      const base = this.getNavigationBase();
      this.goTo(base + delta, {
        direction: delta > 0 ? 'next' : 'previous',
        direct: false,
        userInitiated
      });
    }

    normaliseIndex(index) {
      if (this.loop) return ((index % this.total) + this.total) % this.total;
      return Math.max(0, Math.min(index, this.total - 1));
    }

    cloneSlide(index) {
      const template = this.templates[index];
      if (!template) return null;
      const fragment = template.content.cloneNode(true);
      return fragment.querySelector('[data-adaptive-slide]');
    }


    scheduleInitialAmbientHydration(article) {
      if (!article || this.dataset.ambient === 'off') return;

      // The ambient duplicate is decorative. On the initial mobile viewport the
      // foreground poster already fills the stage, while hydrating this blurred
      // cover image after load can incorrectly become the page's LCP candidate.
      // Keep it completely out of the initial mobile request path; it is still
      // hydrated on user-initiated slide navigation by goTo().
      if (this.device === 'mobile') return;

      const hydrate = () => {
        const run = () => this.hydrateAmbient(article);
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(run, { timeout: 900 });
        } else {
          window.setTimeout(run, 180);
        }
      };

      if (document.readyState === 'complete') hydrate();
      else window.addEventListener('load', hydrate, { once: true, signal: this.signal });
    }

    hydrateAmbient(article) {
      if (!article || this.dataset.ambient === 'off') return;

      const image = article.querySelector('[data-gvs-ambient-image]');
      if (!(image instanceof HTMLImageElement) || image.dataset.gvsAmbientHydrated === 'true') return;

      const picture = image.closest('picture');
      picture?.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset || '';
        delete source.dataset.srcset;
      });

      const reveal = () => image.classList.add('is-loaded');
      image.addEventListener('load', reveal, { once: true });
      image.addEventListener('error', reveal, { once: true });

      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset;
        delete image.dataset.srcset;
      }
      if (image.dataset.src) {
        image.src = image.dataset.src;
        delete image.dataset.src;
      }

      image.dataset.gvsAmbientHydrated = 'true';
      if (image.complete) reveal();
    }

    async waitForPoster(article, timeoutMs = 1800) {
      const image = article?.querySelector('[data-slide-poster] img');
      if (!(image instanceof HTMLImageElement)) return;

      image.loading = 'eager';
      image.fetchPriority = 'high';

      if (!image.complete || image.naturalWidth === 0) {
        await new Promise((resolve) => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            image.removeEventListener('load', finish);
            image.removeEventListener('error', finish);
            window.clearTimeout(timeout);
            resolve();
          };

          const timeout = window.setTimeout(finish, timeoutMs);
          image.addEventListener('load', finish, { once: true });
          image.addEventListener('error', finish, { once: true });
        });
      }

      if (image.complete && image.naturalWidth > 0 && typeof image.decode === 'function') {
        await image.decode().catch(() => {});
      }
    }

    getMediaAspect(article) {
      const video = article?.querySelector('.gvs-adaptive-slideshow__video');
      if (video instanceof HTMLVideoElement && video.videoWidth > 0 && video.videoHeight > 0) {
        return video.videoWidth / video.videoHeight;
      }

      const image = article?.querySelector('[data-slide-poster] img');
      if (!(image instanceof HTMLImageElement)) return 8 / 3;

      if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        return image.naturalWidth / image.naturalHeight;
      }

      const width = Number(image.getAttribute('width')) || 0;
      const height = Number(image.getAttribute('height')) || 0;
      if (width > 0 && height > 0) return width / height;

      return 8 / 3;
    }

    async syncStageToArticle(article, { animate = true } = {}) {
      if (!this.stage) return;

      if (this.device !== 'desktop') {
        this.stage.style.removeProperty('--gvs-as-stage-height');
        return;
      }

      if (!article) return;

      const stageRect = this.stage.getBoundingClientRect();
      const width = stageRect.width || this.clientWidth;
      if (!width) return;

      const aspect = this.getMediaAspect(article);
      if (!Number.isFinite(aspect) || aspect <= 0) return;

      const targetHeight = Math.max(1, Math.round(width / aspect));
      const phases = this.getPhaseDurations();

      if (!animate || this.reducedMotion) {
        this.stage.style.transitionDuration = '0ms';
        this.stage.style.setProperty('--gvs-as-stage-height', `${targetHeight}px`);
        window.requestAnimationFrame(() => this.stage.style.removeProperty('transition-duration'));
        return;
      }

      const currentHeight = stageRect.height;
      if (Math.abs(currentHeight - targetHeight) < 2) {
        this.stage.style.setProperty('--gvs-as-stage-height', `${targetHeight}px`);
        return;
      }

      this.stage.style.setProperty('--gvs-as-stage-height', `${targetHeight}px`);
      await this.waitForTransition(this.stage, 'height', phases.stage);
    }

    getVideoTemplate(article) {
      return article?.querySelector(`template[data-gvs-video-template="${this.device}"]`) || null;
    }

    prepareVideo(article, active = false) {
      if (!article) return null;

      const existing = article.querySelector('.gvs-adaptive-slideshow__video');
      if (existing) {
        if (active) this.activateVideo(existing, article);
        return existing;
      }

      const template = this.getVideoTemplate(article);
      if (!template || template.dataset.hasVideo !== 'true') return null;

      const fragment = template.content.cloneNode(true);
      const video = fragment.querySelector('.gvs-adaptive-slideshow__video');
      if (!video) return null;

      const wantsAutoplay = video.dataset.videoAutoplay === 'true';
      const hasControls = video.dataset.videoControls === 'true';

      // Decorative non-autoplay/non-controlled video has no useful interaction;
      // retain the supplied poster instead of downloading unnecessary media.
      if (!wantsAutoplay && !hasControls) return null;
      if (this.reducedMotion && wantsAutoplay && !hasControls) return null;

      const stack = article.querySelector('[data-media-stack]');
      if (!stack) return null;

      stack.append(fragment);
      video.preload = active || wantsAutoplay || hasControls ? 'auto' : 'metadata';

      if (video.readyState === 0 && (active || wantsAutoplay || hasControls)) {
        video.load();
      }

      if (hasControls) article.classList.add('has-video-controls');

      if (video.dataset.gvsConfigured !== 'true') {
        video.addEventListener('playing', () => this.revealVideoFrame(video, article));
        video.addEventListener('error', () => {
          article.classList.remove('is-video-visible', 'has-video-controls');
        });
        video.addEventListener('loadedmetadata', () => {
          if (article === this.getCurrentArticle() && this.device === 'desktop') {
            this.syncStageToArticle(article, { animate: true }).catch(() => {});
          }
        });
        video.addEventListener('ended', () => {
          if (
            video.dataset.videoLoop !== 'true' &&
            video.dataset.videoAutoplay === 'true' &&
            article === this.getCurrentArticle() &&
            this.autoplayEnabled &&
            !this.userPaused
          ) {
            this.goTo(this.current + 1, { direction: 'next', userInitiated: false });
          }
        });
        video.dataset.gvsConfigured = 'true';
      }

      if (active) this.activateVideo(video, article);
      return video;
    }

    activateVideo(video, article) {
      if (!(video instanceof HTMLVideoElement)) return;

      const wantsAutoplay = video.dataset.videoAutoplay === 'true';
      const hasControls = video.dataset.videoControls === 'true';

      if (hasControls) article.classList.add('has-video-controls');
      if (!wantsAutoplay || !this.canAnimate()) return;

      video.muted = true;
      video.play().then(() => {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          this.revealVideoFrame(video, article);
        }
      }).catch(() => {
        // Poster remains the sharp fallback if browser autoplay is unavailable.
      });
    }

    revealVideoFrame(video, article) {
      if (!article || article !== this.getCurrentArticle()) return;

      const reveal = () => article.classList.add('is-video-visible');
      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(() => window.requestAnimationFrame(reveal));
      } else {
        window.requestAnimationFrame(() => window.requestAnimationFrame(reveal));
      }
    }

    activateArticle(article, { initial = false } = {}) {
      if (!article) return;

      article.removeAttribute('inert');
      article.setAttribute('aria-hidden', 'false');
      const video = this.prepareVideo(article, true);

      if (!video && !initial) {
        article.classList.remove('is-video-visible', 'has-video-controls');
      }
    }

    deactivateArticle(article) {
      if (!article) return;
      article.setAttribute('aria-hidden', 'true');
      article.setAttribute('inert', '');
      this.pauseArticleMedia(article, true);
    }

    pauseArticleMedia(article, reset = true) {
      if (!article) return;

      article.querySelectorAll('video').forEach((video) => {
        if (!video.paused) video.pause();
        if (reset && Number.isFinite(video.duration)) {
          try {
            video.currentTime = 0;
          } catch (_) {
            // Streaming media may not be seekable before metadata is ready.
          }
        }
      });
    }

    playActiveVideo() {
      if (!this.canAnimate()) return;
      const article = this.getCurrentArticle();
      if (!article) return;
      const video = this.prepareVideo(article, true);
      if (video) this.activateVideo(video, article);
    }

    canAnimate() {
      return (
        !this.reducedMotion &&
        !this.userPaused &&
        !this.pointerPaused &&
        this.isInViewport &&
        !document.hidden
      );
    }

    waitForTransition(element, propertyName = 'opacity', duration = 200) {
      if (!element || this.reducedMotion || duration <= 0) return Promise.resolve();

      return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          element.removeEventListener('transitionend', onEnd);
          window.clearTimeout(timeout);
          resolve();
        };
        const onEnd = (event) => {
          if (event.target !== element || event.propertyName !== propertyName) return;
          finish();
        };
        const timeout = window.setTimeout(finish, duration + 140);
        element.addEventListener('transitionend', onEnd);
      });
    }

    async goTo(index, {
      direction = null,
      direct = false,
      userInitiated = false
    } = {}) {
      if (this.total < 1) return;

      if (this.isTransitioning) {
        this.pendingNavigation = { index, options: { direction, direct, userInitiated } };
        return;
      }

      const target = this.normaliseIndex(index);
      if (target === this.current) {
        this.startAutoplay();
        return;
      }

      const article = this.cloneSlide(target);
      if (!article) return;
      this.hydrateAmbient(article);

      this.isTransitioning = true;
      this.transitionTarget = target;
      this.stopAutoplay();
      this.classList.add('is-transitioning');

      article.setAttribute('aria-hidden', 'true');
      article.setAttribute('inert', '');
      this.incomingLayer.replaceChildren(article);
      this.incomingLayer.classList.add('is-foreground-hidden', 'is-ambient-hidden');
      this.incomingLayer.removeAttribute('hidden');

      await this.waitForPoster(article);
      this.prepareVideo(article, false);
      this.applyMotionDuration();

      const phases = this.getPhaseDurations();
      const currentArticle = this.getCurrentArticle();
      const currentStack = currentArticle?.querySelector('[data-media-stack]');
      const incomingAmbient = article.querySelector('.gvs-adaptive-slideshow__ambient');

      // Phase 1: remove the meaningful outgoing foreground first. This prevents
      // two sets of baked-in promotional text/products from ever overlapping.
      this.currentLayer.classList.add('is-foreground-hidden');
      await this.waitForTransition(currentStack, 'opacity', phases.out);

      // Phase 2: while only atmosphere is visible, resize the desktop stage to
      // the incoming creative and dissolve the ambient backdrop beneath it.
      const stagePromise = this.syncStageToArticle(article, { animate: true });
      this.currentLayer.classList.add('is-ambient-hidden');
      this.incomingLayer.classList.remove('is-ambient-hidden');
      const ambientDuration = this.dataset.ambient === 'off' ? 0 : phases.ambient;
      await Promise.all([
        this.waitForTransition(incomingAmbient, 'opacity', ambientDuration),
        stagePromise
      ]);

      const oldArticle = currentArticle;
      this.deactivateArticle(oldArticle);
      this.current = target;

      const oldCurrentLayer = this.currentLayer;
      const newCurrentLayer = this.incomingLayer;

      // Remove the outgoing layer before changing z-index roles; its slide has
      // an opaque section background and must never cover the prepared incoming.
      oldCurrentLayer.replaceChildren();
      oldCurrentLayer.setAttribute('hidden', '');

      newCurrentLayer.classList.remove('gvs-adaptive-slideshow__layer--incoming');
      newCurrentLayer.classList.add('gvs-adaptive-slideshow__layer--current');
      newCurrentLayer.removeAttribute('data-gvs-incoming-layer');
      newCurrentLayer.setAttribute('data-gvs-current-layer', '');

      oldCurrentLayer.classList.remove('gvs-adaptive-slideshow__layer--current');
      oldCurrentLayer.classList.add('gvs-adaptive-slideshow__layer--incoming');
      oldCurrentLayer.removeAttribute('data-gvs-current-layer');
      oldCurrentLayer.setAttribute('data-gvs-incoming-layer', '');
      oldCurrentLayer.classList.remove('is-foreground-hidden', 'is-ambient-hidden');

      this.currentLayer = newCurrentLayer;
      this.incomingLayer = oldCurrentLayer;

      const newArticle = this.getCurrentArticle();
      this.activateArticle(newArticle);

      // Phase 3: the new ambient and geometry are already settled. Reveal only
      // the incoming foreground, giving graphic-heavy banners a clean finish.
      const newStack = newArticle?.querySelector('[data-media-stack]');
      await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
      this.currentLayer.classList.remove('is-foreground-hidden');
      await this.waitForTransition(newStack, 'opacity', phases.incoming);

      this.currentLayer.classList.remove('is-ambient-hidden');
      this.classList.remove('is-transitioning');
      this.updateState(userInitiated);
      this.isTransitioning = false;
      this.transitionTarget = null;
      this.startAutoplay();

      if (this.pendingNavigation) {
        const pending = this.pendingNavigation;
        this.pendingNavigation = null;
        this.goTo(pending.index, pending.options);
      }
    }

    updateState(announce = false) {
      this.dots.forEach((dot, index) => {
        const active = index === this.current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (this.previousButton) this.previousButton.disabled = !this.loop && this.current === 0;
      if (this.nextButton) this.nextButton.disabled = !this.loop && this.current === this.total - 1;

      if (announce && this.status) {
        const article = this.getCurrentArticle();
        this.status.textContent = article?.getAttribute('aria-label') || `Slide ${this.current + 1} of ${this.total}`;
      }
    }

    startAutoplay() {
      this.stopAutoplay();

      if (
        !this.canAutoplaySlides() ||
        this.total < 2 ||
        !this.canAnimate() ||
        this.isTransitioning
      ) return;

      const activeVideo = this.getCurrentArticle()?.querySelector('.gvs-adaptive-slideshow__video');
      const waitsForVideoEnd = Boolean(
        activeVideo &&
        activeVideo.dataset.videoAutoplay === 'true' &&
        activeVideo.dataset.videoLoop !== 'true' &&
        !activeVideo.paused &&
        !activeVideo.ended
      );

      if (waitsForVideoEnd) return;

      this.timer = window.setTimeout(() => {
        this.goTo(this.current + 1, {
          direction: 'next',
          userInitiated: false
        });
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;

      this.autoplayButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      this.autoplayButton.setAttribute(
        'aria-label',
        this.userPaused ? 'Play featured promotions' : 'Pause featured promotions'
      );
    }
  }

  customElements.define('gvs-adaptive-slideshow', GvsAdaptiveSlideshow);
})();
